import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomAppBar from "../../components/AppBar";
import api from "../../globals/axios";
import { makeStyles } from "@mui/styles";
import { useAlert } from "../../components/Alert";
import GenericButton from "../../components/Custom/GenericButton";
import FileUpload from "../../components/Custom/FileUpload";
import CampaignCard from "../../components/Admin/CampaignCard";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    marginBottom: "-4rem",
  },
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    padding: "20px 0",
    borderRadius: "4px",
    marginBottom: "20px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    minHeight: "200px",
  },
  greenLoader: {
    color: "#4CAF50",
  },
}));

export default function Campaigns() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const classes = useStyles();
  const { showAlert } = useAlert();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    summary: "",
    document: null,
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await api.get("/leadgeneration/campaigns/");
      setCampaigns(response.data);
    } catch (err) {
      console.log(err);
      showAlert("error", "Failed to load campaigns. Please try again later!");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      document: file,
    }));
  };

  const handleViewCampaignDetails = (campaignId) => {
    navigate(`/campaign-details/${campaignId}`);
  };

  const handleSubmit = async () => {
    const formPayload = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key]) {
        formPayload.append(key, formData[key]);
      }
    });

    try {
      setLoading(true);
      await api.post("/leadgeneration/campaigns/", formPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showAlert("success", "Campaign created successfully");
      loadCampaigns();
      setFormData({
        title: "",
        summary: "",
        document: null,
      });
    } catch (err) {
      console.log("Error in creating campaigns", err);
      showAlert("error", "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  const renderCampaignList = () => {
    if (loading) {
      return (
        <Box className={classes.loaderContainer}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      );
    }

    if (campaigns.length === 0) {
      return (
        <Typography variant="body1" textAlign="center">
          {t("No campaigns found")}
        </Typography>
      );
    }

    return campaigns.map((campaign) => (
      <Box
        key={campaign.id}
        mt={2}
        onClick={() => handleViewCampaignDetails(campaign.id)}
      >
        <CampaignCard
          title={campaign.title}
          summary={campaign.summary}
          document={campaign.document}
          isActive={campaign.is_active}
        />
      </Box>
    ));
  };

  return (
    <Box className={classes.root} component={Paper}>
      <CustomAppBar />

      <Box className={classes.container}>
        <Paper className={classes.title} elevation={0}>
          <Typography variant="h5">{t("Campaign")}</Typography>
        </Paper>

        <Box mt={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("Title")}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("Summary")}
                name="summary"
                value={formData.summary}
                onChange={handleInputChange}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={8} sm={4} md={3}>
                <FileUpload
                  label="Document"
                  name="document"
                  accept=".pdf"
                  onChange={handleFileChange}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={8} sm={4} md={3}>
                <GenericButton
                  backgroundColor={"#4CAF50"}
                  fontColor={"#fff"}
                  fontSize={"14px"}
                  padding={"7px 10px"}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {t("Create Campaign")}
                </GenericButton>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Box mt={4}>
          <Paper className={classes.title} elevation={0}>
            <Typography variant="h5">{t("My Campaigns")}</Typography>
          </Paper>
          {renderCampaignList()}
        </Box>
      </Box>
    </Box>
  );
}
